/**
 * Included to document how ViewApplicationData could be further broken down,
 * given the specific requests we have had in the past. However, we still
 * strongly prefer a unified permission for viewing application data, for
 * simplicity and because these subdivisions would enable users who want
 * to abuse the payment model to do so by sharing an operation (although
 * not that easily, since only one farm would get to view harvest data)
 */
enum _ViewApplicationData {
  /**
   * Just viewing data inside panorama for non-harvest application data,
   * including map-viewing, reports, etc.
   */
  ViewNonHarvestApplicationData,
  /**
   * Focused on harvest data, still only in Panorama
   */
  ViewHarvestData,
  /**
   * View live stream data from connected machinery, not in Panorama
   * In theory we might want to separate this out from the others if
   * we wanted to allow supportability even when we don't trust long-
   * term usage
   */
  ViewLiveStreamData
}

/**
 * Permissions that can be granted at the operation level. This is intended to
 * be exhaustive, and it's better to add more specific permissions here than to
 * pigeonhole things into existing permissions that are not quite right.
 *
 * NOTE: This is a runtime enum only and should not be persisted, since we want
 *  to be able to adapt quickly to changing requirements without a migration.
 */
export enum OperationLevelPermission {
  /**
   * Anything that requires read-only access to application data that has
   * been uploaded to the operation. Map-viewing, reports, etc.
   */
  ViewApplicationData,
  /**
   * File-based import of application data
   */
  UploadApplicationData,
  /**
   * Editing cff names, season, crop, hybrid names, etc. of application data
   * after the fact. Also deletion of application data.
   */
  ManageApplicationData,

  /**
   * Pair/unpair connected DBMs.
   */
  ManageDBMPairing,
  /**
   * Editing machine settings remotely, including pushing config changes,
   * calibrations, software updates, etc.
   */
  EditMachineSettings,

  /**
   * Whether the user's token, stored on the DBM, is allowed to sync data
   * to the current operation. This is distinct from UploadApplicationData,
   * because it is not about the user's current status in the operation, but
   * just whether the dbm was legitimately paired to this operation.
   */
  SyncDataFromMachine,

  /**
   * Viewing and adding scouting notes, photos, etc. to fields. Including modifying the
   * notes that you have uploaded yourself.
   */
  Scouting,

  /**
   * Viewing all planning related data, including planned field list, rx, plans, etc.
   */
  ViewPlans,
  /**
   * Creating, editing or deleting existing plans, including modifying boundaries, field lists, prescriptions,
   * etc.
   */
  ManagePlans,

  /**
   * Invite, change permissions, and remove users from the organization.
   */
  ManageUsers,
  /**
   * Billing-related actions, including viewing invoices, updating payment methods, etc.
   */
  PayInvoices,
  /**
   * Rename the organization, delete the organization, etc.,
   */
  ManageOperationDetails,
  /**
   * Exporting data from the operation, including application data, scouting data, etc.
   * This permission is useful for restricting data exfiltration when on a trial plan.
   */
  ExportData,
  /**
   * Managing data sharing settings with third-party integrations, advisors, and accepting
   * or rejecting terms of service for specific features.
   */
  ManageDataSharing
}

/**
 * Permissions that can be granted at the platform level.
 */
export enum PlatformWidePermission {
  /**
   * Administer the entire platform as a super-admin
   */
  AdministerPlatform,

  /**
   * Support users across all organizations that have
   * opted in to support access.
   */
  SupportUsers,

  /**
   * Perform actions allowed to any user, regardless of role or organization.
   */
  PerformBasicUserActions
}

/**
 * For endpoints whose access is NOT gated by a Panorama user permission. These
 * are authenticated by a mechanism outside the Panorama permission model — an
 * external-identity token (e.g. a partner user pool), a custom gateway
 * authorizer, or IAM — so no `OperationLevelPermission` / `PlatformWidePermission`
 * meaningfully applies.
 *
 * Permission-aware consumers (AI tool generation, client-side gating) must treat
 * these as *not* offerable via a Panorama permission rather than mapping them to
 * an arbitrary one.
 */
export enum ExternalAuthorization {
  /**
   * Authenticated by a non-Panorama identity (e.g. a partner user pool token),
   * verified by the gateway/service rather than by Panorama claims.
   */
  NonPanoramaCaller = 'non_panorama_caller'
}
